import React, { useEffect, useState } from 'react';
import Icon from '../../../hive/ui/Icon';
import { Thumb, Modal, Select, Confirm, assetUrl } from '../ui';
import useConfirmRemove from './useConfirmRemove';
import { mediaUrl } from '../data/media';
import { PHOTO_TAGS, MAIN_PHOTO_TAG } from '../data/constants';
import { tr } from '../i18n';

/**
 * Photo grid with a lightbox, and the video grid (spec § 7).
 *
 * Re-tagging a photo as the main product photo demotes the previous one —
 * that bookkeeping lives in `applyMainPhotoTag` (rule Q1) and is applied by
 * the caller, so this component only reports the intent.
 */

export function PhotoGrid({ photos = [], onRetag, onRemove, products = [] }) {
  const [open, setOpen] = useState(-1);
  // A photo cannot be retaken once the buyer has left the fair, so removal is
  // always confirmed — see useConfirmRemove.
  const confirm = useConfirmRemove();
  if (!photos.length) return null;

  return (
    <>
      <div className="ft-mediagrid">
        {photos.map((p, i) => (
          <div className="ft-mediacell" key={p.id}>
            <button type="button" className="ft-mediabtn" onClick={() => setOpen(i)}>
              <Thumb photo={p} size={84} rounded={false} alt={p.tag || 'Photo'} />
            </button>
            <span className="ft-mediatag">{p.tag || 'Product'}</span>
            {onRemove && (
              <button
                type="button"
                className="ft-mediadel"
                aria-label={tr("Remove photo")}
                onClick={() => confirm.ask(p, () => onRemove(p))}
              >
                <Icon name="X" size={13} />
              </button>
            )}
          </div>
        ))}
      </div>

      {open >= 0 && photos[open] && (
        <Lightbox
          photos={photos}
          index={open}
          products={products}
          onIndex={setOpen}
          onClose={() => setOpen(-1)}
          onRetag={onRetag}
          onRemove={
            onRemove
              ? (p) =>
                  confirm.ask(p, () => {
                    onRemove(p);
                    setOpen(-1);
                  })
              : null
          }
        />
      )}

      {confirm.pending && (
        <Confirm
          title={tr("Remove this photo?")}
          message={tr("It is deleted from this device and from the server. A photo taken at a booth cannot be retaken later.")}
          confirmLabel={tr("Remove photo")}
          onConfirm={confirm.accept}
          onCancel={confirm.cancel}
        />
      )}
    </>
  );
}

function Lightbox({ photos, index, onIndex, onClose, onRetag, onRemove, products }) {
  const photo = photos[index];
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const id = photo && (photo.mediaId || photo.id);
    if (!id) {
      setUrl(photo && photo.path ? assetUrl(photo.path) : null);
      return undefined;
    }
    mediaUrl(id).then((u) => {
      if (!cancelled) setUrl(u || (photo.path ? assetUrl(photo.path) : null));
    });
    return () => {
      cancelled = true;
    };
  }, [photo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') onIndex(Math.min(photos.length - 1, index + 1));
      if (e.key === 'ArrowLeft') onIndex(Math.max(0, index - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, photos.length, onIndex]);

  return (
    <div className="ft-lightbox" role="dialog" aria-modal="true">
      <header className="ft-lightbox-bar">
        <span>
          {photo.tag || 'Product'} · {index + 1}/{photos.length}
        </span>
        <button type="button" className="ft-icon-btn ft-on-dark" onClick={onClose} aria-label={tr("Close")}>
          <Icon name="X" size={20} />
        </button>
      </header>

      <div className="ft-lightbox-stage">
        <button
          type="button"
          className="ft-lightbox-nav"
          disabled={index === 0}
          onClick={() => onIndex(index - 1)}
          aria-label="Previous"
        >
          <Icon name="ChevronLeft" size={26} />
        </button>
        {url ? <img src={url} alt={photo.tag || ''} /> : <div className="ft-lightbox-blank" />}
        <button
          type="button"
          className="ft-lightbox-nav"
          disabled={index >= photos.length - 1}
          onClick={() => onIndex(index + 1)}
          aria-label={tr("Next")}
        >
          <Icon name="ChevronRight" size={26} />
        </button>
      </div>

      <footer className="ft-lightbox-foot">
        {onRetag && (
          <>
            <Select
              value={photo.tag || 'Product'}
              options={PHOTO_TAGS}
              onChange={(tag) => onRetag(photo, tag)}
            />
            {products.length > 1 && (
              <Select
                value={photo.productId || ''}
                placeholder={tr("Not linked to a product")}
                options={products.map((p) => ({ value: p.id, label: p.name }))}
                onChange={(productId) => onRetag(photo, photo.tag, productId)}
              />
            )}
          </>
        )}
        {onRemove && (
          <button type="button" className="ft-btn ft-btn-danger" onClick={() => onRemove(photo)}>
            {tr("Remove")}
          </button>
        )}
      </footer>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Videos
   ═════════════════════════════════════════════════════════════════════════ */

export function VideoGrid({ videos = [], onRemove }) {
  const [playing, setPlaying] = useState(null);
  const confirm = useConfirmRemove();
  if (!videos.length) return null;

  return (
    <>
      <div className="ft-mediagrid">
        {videos.map((v) => (
          <div className="ft-mediacell" key={v.id}>
            <button type="button" className="ft-mediabtn ft-videocell" onClick={() => setPlaying(v)}>
              <VideoThumb video={v} />
              <span className="ft-playbadge">
                <Icon name="Play" size={16} />
              </span>
            </button>
            <span className="ft-mediatag">{v.duration ? `${Math.round(v.duration)}s` : 'Video'}</span>
            {onRemove && (
              <button
                type="button"
                className="ft-mediadel"
                aria-label={tr("Remove video")}
                onClick={() => confirm.ask(v, () => onRemove(v))}
              >
                <Icon name="X" size={13} />
              </button>
            )}
          </div>
        ))}
      </div>

      {playing && (
        <Modal title={tr("Video")} onClose={() => setPlaying(null)}>
          <VideoPlayer video={playing} />
        </Modal>
      )}

      {confirm.pending && (
        <Confirm
          title={tr("Remove this video?")}
          message={tr("It is deleted from this device and from the server, and cannot be re-recorded after the fair.")}
          confirmLabel={tr("Remove video")}
          onConfirm={confirm.accept}
          onCancel={confirm.cancel}
        />
      )}
    </>
  );
}

function VideoThumb({ video }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const id = video.mediaId || video.id;
    mediaUrl(id).then((u) => {
      if (!cancelled) setUrl(u || (video.path ? assetUrl(video.path) : null));
    });
    return () => {
      cancelled = true;
    };
  }, [video]);

  if (!url) return <span className="ft-thumb ft-thumb-fallback" style={{ width: 84, height: 84 }} />;
  return <video src={url} muted playsInline preload="metadata" width={84} height={84} />;
}

function VideoPlayer({ video }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    mediaUrl(video.mediaId || video.id).then((u) => {
      if (!cancelled) setUrl(u || (video.path ? assetUrl(video.path) : null));
    });
    return () => {
      cancelled = true;
    };
  }, [video]);

  if (!url) return <p className="ft-sub">{tr("This video is not available on this device.")}</p>;
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <video src={url} controls playsInline style={{ width: '100%', borderRadius: 10 }} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Voice notes
   ═════════════════════════════════════════════════════════════════════════ */

export function VoiceList({ notes = [], onRemove }) {
  const confirm = useConfirmRemove();
  if (!notes.length) return null;
  return (
    <>
    <ul className="ft-voicelist">
      {notes.map((n) => (
        <li key={n.id}>
          <VoicePlayer note={n} />
          <span className="ft-faint">{n.duration ? `${n.duration}s` : ''}</span>
          {onRemove && (
            <button
              type="button"
              className="ft-icon-btn"
              onClick={() => confirm.ask(n, () => onRemove(n))}
              aria-label="Remove voice note"
            >
              <Icon name="Trash2" size={15} />
            </button>
          )}
        </li>
      ))}
    </ul>
    {confirm.pending && (
      <Confirm
        title={tr("Remove this voice note?")}
        message={tr("What was said at the booth is not recoverable once this is deleted.")}
        confirmLabel={tr("Remove note")}
        onConfirm={confirm.accept}
        onCancel={confirm.cancel}
      />
    )}
    </>
  );
}

function VoicePlayer({ note }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    mediaUrl(note.mediaId || note.id).then((u) => {
      if (!cancelled) setUrl(u || (note.path ? assetUrl(note.path) : null));
    });
    return () => {
      cancelled = true;
    };
  }, [note]);

  if (!url) return <span className="ft-sub">{tr("Not on this device")}</span>;
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio src={url} controls preload="metadata" style={{ flex: 1, minWidth: 0 }} />;
}

export { MAIN_PHOTO_TAG };
